import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Button, CircularProgress, Paper, styled } from "@mui/material";
import { type ReactNode } from "react";

interface TaskCardProps {
  isSelected?: boolean;
  onClick?: () => void;
  onComplete?: (event: React.MouseEvent) => void;
  isCompleting?: boolean;
  children: ReactNode;
}

const StyledTaskCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  "&::before": isSelected
    ? {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "50%",
        content: '""',
        height: "10px",
        position: "absolute",
        right: "10px",
        top: "10px",
        width: "10px",
      }
    : {},

  "&:hover": {
    boxShadow: theme.shadows[5],
    transform: isSelected ? "scale(1.02)" : "scale(1.01)",
  },

  backgroundColor: isSelected ? theme.palette.action.selected : theme.palette.background.paper,

  borderLeft: isSelected ? `5px solid ${theme.palette.primary.main}` : "none",

  boxShadow: isSelected ? theme.shadows[8] : theme.shadows[1],

  cursor: "pointer",

  // Center the card within its container
  display: "flex",

  flexDirection: "column",

  height: "280px",

  // Fixed width for all cards
  margin: "0 auto",

  padding: theme.spacing(2.5),

  position: "relative",

  transform: isSelected ? "scale(1.02)" : "scale(1)",

  transition: "all 0.3s ease",
  // Fixed height for all cards
  width: "320px",
}));

export const TaskCard = ({ isSelected, onClick, onComplete, isCompleting = false, children }: TaskCardProps) => {
  const handleCompleteClick = (event: React.MouseEvent) => {
    // Stop propagation to prevent the card click event from firing
    event.stopPropagation();
    if (onComplete && !isCompleting) {
      onComplete(event);
    }
  };

  return (
    <StyledTaskCard isSelected={isSelected} onClick={onClick}>
      {children}

      {/* Complete button - positioned at the bottom right */}
      {onComplete && (
        <Box
          sx={{
            alignItems: "center",
            bottom: "16px",
            display: "flex",

            // Ensure vertical centering
            height: "32px",

            position: "absolute",

            // Match the footer spacing
            right: "16px",
            zIndex: 2, // Match the height we set for the due date
          }}
        >
          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={!isCompleting && <CheckCircleOutlineIcon />}
            onClick={handleCompleteClick}
            disabled={isCompleting}
            sx={{
              "&:hover": {
                backgroundColor: "success.light",
                color: "common.white",
              },
              borderRadius: "20px",
              minWidth: "auto",
            }}
          >
            {isCompleting ? (
              <Box sx={{ alignItems: "center", display: "flex" }}>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Completing...
              </Box>
            ) : (
              "Complete"
            )}
          </Button>
        </Box>
      )}
    </StyledTaskCard>
  );
};
